import { SxProps, Theme } from "@mui/material";

import type { BucketKey } from "components/buckets/BucketToggle";

// The page's own type face and accent navy, which every style here inherits
// rather than restating: the chart section is part of the Strategy Report and
// has to read as it, not as a component from somewhere else.
const FONT_FAMILY = '"Poppins", sans-serif';
const ACCENT = "#003057";
const MUTED = "#666";

// Common style objects for ChartsReport
export const chartsReportStyles = {
  // Container styles
  container: {
    width: "100%",
    mt: 2,
    mb: 2,
    backgroundColor: "white",
    borderRadius: 2,
    border: "1px solid #E0E0E0",
    padding: 2,
  } as SxProps<Theme>,

  // Title container styles
  titleContainer: {
    display: "flex",
    alignItems: "center",
    mb: 0.5,
  } as SxProps<Theme>,

  // Accent rectangle styles
  accentRectangle: {
    width: "4px",
    height: "20px",
    backgroundColor: ACCENT,
    marginRight: 1.5,
    borderRadius: "2px",
  } as SxProps<Theme>,

  // Title text styles
  title: {
    color: ACCENT,
    fontWeight: "bold",
    fontSize: "1.25rem",
    fontFamily: FONT_FAMILY,
  } as SxProps<Theme>,

  // Subtitle styles
  subtitle: {
    color: MUTED,
    fontSize: "0.8rem",
    mb: 2,
    ml: 6, // Align with title text (accent + margin)
    fontFamily: FONT_FAMILY,
  } as SxProps<Theme>,

  // The row of per-bucket series drop-downs above the charts.
  selectorRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    alignItems: "center",
    mb: 2,
  } as SxProps<Theme>,

  // Shown in place of the charts when nothing is selected, or before the
  // report has been calculated at all.
  placeholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 220,
    border: "2px dashed #E0E0E0",
    borderRadius: 2,
    backgroundColor: "#fafafa",
    color: MUTED,
    fontFamily: FONT_FAMILY,
    textAlign: "center",
    px: 2,
  } as SxProps<Theme>,
};

// Chart configuration constants for the report's Chart.js line charts.
export const chartConfig = {
  height: 300,
  lineColor: "#2C8BB0",
  // Chart.js' monotone cubic interpolation, which is x-charts' "monotoneX"
  // under another name: a smoothed line that never overshoots a data point,
  // so a smoothed performance index cannot draw a high the series never hit.
  curve: "monotone" as const,
  tension: 0.4,
};

/**
 * One accent per bucket, so a chart's colour ties it to the drop-down that
 * asked for it. All three are colours the report already uses — the two
 * series colours the chart section shipped with, and the page's title navy —
 * rather than a new palette beside the existing one.
 */
export const bucketSeriesColors: Record<BucketKey, string> = {
  "1": chartConfig.lineColor,
  "2": "#F57C00",
  "3": ACCENT,
};

/**
 * The colours the individual securities draw in, in the order they are handed
 * out. Every line now shares ONE set of axes, so a security can no longer
 * wear its bucket's accent — three securities of Bucket 2 in the bucket's
 * orange would be three indistinguishable lines.
 *
 * These four are what survived checking the whole on-screen set — these plus
 * the three bucket accents above — pairwise for colour-vision deficiency and
 * for normal vision: worst pair ΔE 15.4 normal (clear of the 15 floor) and
 * 7.9 under protanopia, which is legal only alongside a secondary encoding of
 * identity. That is why the chart ships a legend AND names every series in
 * its tooltip: identity is never carried by colour alone. Deliberately NOT
 * the report's own yellow-orange or pink family — both collide with Bucket
 * 2's orange or Bucket 1's teal badly enough to fail outright.
 */
export const memberSeriesColors = [
  "#4A3AA7", // violet
  "#1BAF7A", // aqua
  "#008300", // green
  "#6D4C41", // brown
];

/**
 * The stroke patterns a security's colour cycles THROUGH once the four above
 * are spent, so the 5th security is a dashed violet rather than a second
 * solid violet. Four colours times three patterns is twelve securities told
 * apart, and the legend draws each swatch with its own pattern so the key
 * matches the line.
 */
export const seriesDashPatterns = ["", "7 4", "2 3"];

/**
 * The stroke Bucket 1's "Only Stocks" line is drawn in (operator, task 3576).
 *
 * The bucket's OWN accent carries that line's colour — it is a second
 * whole-bucket aggregate, not a security — so the dash is the whole of what
 * tells it from the bucket's other line. Same colour, never the same stroke:
 * the two ARE the same bucket measured two ways, and a reader comparing them
 * should see that relationship rather than two unrelated colours.
 *
 * Deliberately one of the patterns above rather than a fourth of its own, so
 * a dashed bucket line and a dashed security read as the same kind of mark on
 * one chart; identity is carried by the legend and the tooltip, never by
 * stroke alone.
 */
export const stocksSeriesDash = seriesDashPatterns[1];

export const bucketSeriesSelectStyles = {
  field: { display: "flex", flexDirection: "column", gap: 0.5 } as SxProps<Theme>,

  label: {
    color: ACCENT,
    fontFamily: FONT_FAMILY,
    fontWeight: 600,
    fontSize: "0.8rem",
  } as SxProps<Theme>,

  // Capped as well as floored. A control now carries two buckets' selections
  // at once, and the closed field names every one of them — left to grow, the
  // Tax-Loss Harvest field would run the width of the section on a full
  // selection. MUI already clips and ellipsises `.MuiSelect-select`, so the
  // cap is all that is needed, and the menu still shows the full list.
  control: {
    minWidth: 220,
    maxWidth: 360,
    backgroundColor: "white",
  } as SxProps<Theme>,

  // The separator under All Securities. See BucketSeriesSelect for why it is
  // a border on that row and not a <Divider> beneath it.
  ruledOff: {
    borderBottom: "1px solid #E0E0E0",
    mb: 0.5,
    pb: 0.5,
  } as SxProps<Theme>,

  // The separator between one BUCKET'S rows and the next bucket's, in a
  // control that carries more than one. Heavier than the rule above so the
  // two read as what they are: a bucket boundary, not another row boundary
  // inside the same bucket.
  sectionEnd: {
    borderBottom: "2px solid #BDBDBD",
    mb: 0.75,
    pb: 0.75,
  } as SxProps<Theme>,
};

/**
 * Percentages, not money: a performance chart is an index that opens at 100%.
 * Trimmed through Number() so a series that moves whole points reads "121%"
 * while one that barely moves still separates "100.05%" from "100.12%" —
 * a fixed 2dp would clutter the first and a fixed 0dp would flatten every
 * tick of the second onto the same label.
 */
export const performanceYAxisConfig = {
  valueFormatter: (value: number) => `${Number(value.toFixed(2))}%`,
};

/**
 * The type face the plot's own tick labels are drawn in.
 *
 * A canvas inherits no CSS, so the font the rest of the section gets from the
 * page has to be handed to Chart.js explicitly — left out, the axis labels
 * render in the library's default sans and the plot reads as a component from
 * somewhere else.
 */
export const performanceTickFont = {
  color: MUTED,
  font: { family: FONT_FAMILY, size: 11 },
};

export const performanceChartStyles = {
  section: { width: "100%", mb: 3 } as SxProps<Theme>,

  // The canvas' own box. Chart.js sizes itself to its parent, so the height
  // lives here rather than on the chart, and `position: relative` is what the
  // tooltip below is positioned against — its coordinates come from Chart.js
  // and are measured from the canvas' top-left corner.
  plot: {
    position: "relative",
    width: "100%",
    height: chartConfig.height,
  } as SxProps<Theme>,

  // Only what Chart.js does not reserve for itself. It measures the axis
  // labels and lays out the plot around them, where x-charts had to be told
  // the margin — so all that is left is half a date label's overhang past the
  // last tick, which would otherwise clip "08/27/2026" to "08/27/20".
  padding: { top: 8, right: 24, bottom: 0, left: 0 },

  // The mockup's soft fill under a lone line. On a canvas the fill IS a
  // colour, so the opacity is mixed into it (withAlpha) rather than set as a
  // separate `fill-opacity` the way an SVG path took it.
  fillOpacity: 0.12,

  // The axis rule and the horizontal guides behind the lines, both kept light
  // enough to sit behind the series rather than compete with them.
  axisColor: "#E0E0E0",
  gridColor: "#F0F0F0",

  // Where the tooltip panel hangs. Positioned by the component from Chart.js'
  // caret coordinates, and out of the pointer's way so moving toward the panel
  // cannot make the chart think the pointer left the plot.
  tooltipAnchor: {
    position: "absolute",
    pointerEvents: "none",
    zIndex: 2,
  } as SxProps<Theme>,

  // The reasons for the series the reader selected and the backend refused.
  // Below the plot rather than in place of it: one refused security no longer
  // costs its own chart, so the lines that DID come back still draw.
  unavailableList: {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    mt: 1.5,
  } as SxProps<Theme>,

  // A refused series named beside its reason, now that it no longer occupies
  // a chart of its own to say it in.
  unavailableRow: {
    color: MUTED,
    fontFamily: FONT_FAMILY,
    fontSize: "0.8rem",
    px: 1,
  } as SxProps<Theme>,

  // The key under the plot, and real DOM rather than the chart library's own
  // legend: this one wraps onto as many rows as the selection needs, draws
  // each swatch with its line's dash pattern, and — being HTML and not pixels
  // on a canvas — can be read out by a screen reader.
  legend: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    columnGap: 2.5,
    rowGap: 0.5,
    // It is a <ul> for the semantics, not for the bullets and the indent the
    // user agent gives one. `m: 0` comes BEFORE the margin below it: sx
    // resolves a shorthand and its longhand in key order, so reversing these
    // two would flatten the top margin back to zero.
    listStyle: "none",
    m: 0,
    // Clear of the axis labels the plot's own bottom margin reserves.
    mt: 0.5,
    // Also what replaces the user agent's list indent.
    px: 1,
  } as SxProps<Theme>,

  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
  } as SxProps<Theme>,

  legendLabel: {
    color: ACCENT,
    fontFamily: FONT_FAMILY,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  tooltip: {
    backgroundColor: "white",
    border: "1px solid #E0E0E0",
    borderRadius: 2,
    boxShadow: "0 4px 16px rgba(0, 48, 87, 0.12)",
    p: 1.5,
    minWidth: 200,
  } as SxProps<Theme>,

  tooltipDate: {
    color: ACCENT,
    fontFamily: FONT_FAMILY,
    fontWeight: "bold",
    fontSize: "0.8rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    mb: 1,
  } as SxProps<Theme>,

  // The tooltip is a TABLE now that a day can carry several series: a swatch
  // and the series' name down the left, the day's move and the compounded
  // total in their own columns. Columns rather than a "label: value" pair per
  // figure, so the reader compares one series against the next straight down
  // the column instead of hunting for the same label in each block.
  tooltipGrid: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
    alignItems: "baseline",
    columnGap: 1.5,
    rowGap: 0.5,
  } as SxProps<Theme>,

  // The swatch tying a tooltip row to its line. A rule rather than a dot, so
  // it can carry the line's dash pattern as well as its colour.
  tooltipSwatch: { width: 14, height: 3, borderRadius: "2px", mt: "0.4em" } as SxProps<Theme>,

  tooltipColumnHeader: {
    color: MUTED,
    fontFamily: FONT_FAMILY,
    fontSize: "0.7rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    textAlign: "right",
  } as SxProps<Theme>,

  tooltipSeriesName: {
    color: ACCENT,
    fontFamily: FONT_FAMILY,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  tooltipFigure: {
    color: ACCENT,
    fontFamily: FONT_FAMILY,
    fontWeight: 600,
    fontSize: "0.8rem",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  } as SxProps<Theme>,
};
